import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  signOutEverywhere,
  type SignOutRedirectConfig,
} from "./signOutEverywhere";

export type BaseAuthUser = {
  email: string;
  role: string;
  sub: string;
  /** From access token `cognito:groups` (same as main app `cognitoUserGroup`). */
  cognitoGroups: string[];
  username?: string;
  name?: string;
  isSsoUser?: boolean;
};

export interface User extends BaseAuthUser {
  firstName?: string;
  lastName?: string;
  userID?: number | string;
  roles?: string[];
  preferences?: unknown[];
  dateCreated?: string;
  externalCustomerID?: string;
  isPasswordResetRequired?: boolean;
  isUserInactive?: boolean;
}

export type AuthSession = Awaited<ReturnType<typeof fetchAuthSession>>;

export type AuthValidationResult =
  | { ok: true }
  | { ok: false; message: string };

export type ValidateAuthenticatedUser = (params: {
  session: AuthSession;
  user: User;
}) => Promise<AuthValidationResult>;

export type FetchUserData = (params: {
  session: AuthSession;
  baseUser: BaseAuthUser;
}) => Promise<Partial<User>>;

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  validationError: string | null;
  refreshAuth: () => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({
  children,
  fetchUserData,
  validateAuthenticatedUser,
  signOutRedirect,
}: {
  children: ReactNode;
  fetchUserData?: FetchUserData;
  validateAuthenticatedUser?: ValidateAuthenticatedUser;
  signOutRedirect?: SignOutRedirectConfig;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    void checkAuth();
  }, [fetchUserData, validateAuthenticatedUser]);

  async function checkAuth() {
    try {
      const session = await fetchAuthSession();
      if (session.tokens) {
        const attributes = await fetchUserAttributes();
        const groupsClaim =
          session.tokens.accessToken?.payload?.["cognito:groups"];
        const cognitoGroups = Array.isArray(groupsClaim)
          ? groupsClaim.filter((g): g is string => typeof g === "string")
          : [];
        const currentUser = await getCurrentUser();
        const identities = session.tokens.idToken?.payload?.identities;
        const baseUser: BaseAuthUser = {
          email: attributes.email || "",
          role: attributes["custom:role"] || "",
          sub: attributes.sub || "",
          cognitoGroups,
          username: currentUser.username,
          name:
            typeof session.tokens.idToken?.payload?.name === "string"
              ? session.tokens.idToken.payload.name
              : undefined,
          isSsoUser: Array.isArray(identities) && identities.length > 0,
        };
        const userData = fetchUserData
          ? await fetchUserData({ session, baseUser })
          : {};
        const nextUser: User = {
          ...baseUser,
          ...userData,
          cognitoGroups: userData.cognitoGroups ?? baseUser.cognitoGroups,
        };

        if (validateAuthenticatedUser) {
          const result = await validateAuthenticatedUser({
            session,
            user: nextUser,
          });

          if (!result.ok) {
            await signOut();
            setValidationError(result.message);
            setIsAuthenticated(false);
            setUser(null);
            return;
          }
        }

        setValidationError(null);
        setUser(nextUser);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSignIn() {
    try {
      setValidationError(null);
      await signInWithRedirect();
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === "UserAlreadyAuthenticatedException"
      ) {
        await checkAuth();
      } else {
        throw error;
      }
    }
  }

  async function handleSignOut() {
    await signOutEverywhere(signOutRedirect);
    await checkAuth();
  }

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    validationError,
    refreshAuth: checkAuth,
    user,
    signIn: handleSignIn,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
